import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  experienceStorage,
  STORAGE_KEYS,
} from '@experiences/blocs/utils/storage';

interface ExperienceUIState {
  bottomNavHeight: number;
  isBottomNavHidden: boolean;
  /** Mobile / tablet-portrait slide-over drawer open state. Not persisted —
   *  transient UI state reset on reload. Hoisted out of template-local
   *  `useState` so microproducts can trigger it via postMessage. */
  isMobileDrawerOpen: boolean;
  /** Desktop inline sidebar collapse state. Persisted. */
  isSidebarCollapsed: boolean;
}

interface ExperienceUIActions {
  setBottomNavHeight: (height: number) => void;
  setBottomNavHidden: (hidden: boolean) => void;
  setMobileDrawerOpen: (open: boolean) => void;
  setSidebarCollapsed: (isCollapsed: boolean) => void;
  toggleMobileDrawer: () => void;
  toggleSidebar: () => void;
}

type ExperienceUIStore = ExperienceUIState & ExperienceUIActions;

export const useExperienceUIStore = create<ExperienceUIStore>()(
  persist(
    (set) => ({
      bottomNavHeight: 0,
      isBottomNavHidden: false,
      isMobileDrawerOpen: false,
      isSidebarCollapsed: false,
      setBottomNavHeight: (height) => set({ bottomNavHeight: height }),
      setBottomNavHidden: (hidden) => set({ isBottomNavHidden: hidden }),
      setMobileDrawerOpen: (open) => set({ isMobileDrawerOpen: open }),
      setSidebarCollapsed: (isCollapsed) =>
        set({ isSidebarCollapsed: isCollapsed }),
      toggleMobileDrawer: () =>
        set((state) => ({ isMobileDrawerOpen: !state.isMobileDrawerOpen })),
      toggleSidebar: () =>
        set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
    }),
    {
      name: STORAGE_KEYS.UI_STATE,
      // Persist only the collapse state; drawer is transient.
      partialize: (state) => ({ isSidebarCollapsed: state.isSidebarCollapsed }),
      storage: createJSONStorage(() => ({
        getItem: (key) => experienceStorage.getString(key) ?? null,
        removeItem: (key) => experienceStorage.remove(key),
        setItem: (key, value) => experienceStorage.set(key, value),
      })),
    },
  ),
);
